import React from 'react';
import type {
    SearchSuggestion,
    SearchSuggestionGroup,
} from '../view-models/search-suggestion';
import { SearchSuggestionList } from './SearchSuggestionList';
import { SearchSuggestionMix } from './SearchSuggestionMix';

interface SearchSuggestionContentProps {
    suggestionGroups: SearchSuggestionGroup[];
    query: string;
    onSearchSuggestionClick: (suggestion: SearchSuggestion | string) => void;
    variant?: 'default' | 'header';
    menuColor?: 'light' | 'dark';
    displayMode?: 'grouped' | 'mixed';
}

/**
 * Helper to read the search suggestion display mode from the URL.
 */
const getDisplayModeFromUrl =
    (): SearchSuggestionContentProps['displayMode'] => {
        if (typeof window === 'undefined') {
            return undefined;
        }

        const param = new URLSearchParams(window.location.search).get(
            'searchSuggestionDisplayMode'
        );

        if (param === 'grouped' || param === 'mixed') {
            return param;
        }

        return undefined;
    };

/**
 * Component for displaying search suggestion content.
 * Supports both grouped (separate sections) and mixed (all together) display modes.
 */
export const SearchSuggestionContent: React.FC<
    SearchSuggestionContentProps
> = ({
    suggestionGroups,
    query,
    onSearchSuggestionClick,
    variant = 'default',
    menuColor = 'dark',
    displayMode,
}) => {
    const resolvedDisplayMode =
        displayMode ?? getDisplayModeFromUrl() ?? 'grouped';

    if (resolvedDisplayMode === 'mixed') {
        return (
            <SearchSuggestionMix
                groups={suggestionGroups}
                query={query}
                variant={variant}
                menuColor={menuColor}
                onOptionClick={onSearchSuggestionClick}
            />
        );
    }

    // Grouped mode - show each group separately
    return (
        <div className="flex flex-col gap-4">
            {suggestionGroups.map((group, groupIndex) => (
                <SearchSuggestionList
                    key={groupIndex}
                    group={group}
                    query={query}
                    variant={variant}
                    menuColor={menuColor}
                    onOptionClick={(suggestion) =>
                        onSearchSuggestionClick(suggestion)
                    }
                />
            ))}
        </div>
    );
};
